import React from 'react';

import { Button } from '@splitfiesta/ui';

import { toast } from '@/utils/toast';

interface LockedActionProps {
  /** Why the action is unavailable — shown as the tooltip and the toast. */
  message: string;
  /** Button content: an icon, a label, or both. */
  children: React.ReactNode;
  testId?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * An action the viewer can see but not use. It stays on screen — a
 * control that silently disappears reads as a broken page — styled as
 * disabled, with the reason as its tooltip and as a toast on click.
 *
 * The button is `aria-disabled`, not `disabled`: a truly disabled button
 * leaves the tab order and answers nothing, so a keyboard or
 * screen-reader user would get no tooltip, no toast and no explanation
 * at all — the exact failure this control exists to prevent.
 */
export function LockedAction({
  message,
  children,
  testId,
  className = '',
  size = 'sm',
}: LockedActionProps) {
  return (
    <Button
      variant="ghost"
      size={size}
      aria-disabled="true"
      onClick={() => toast.info(message)}
      className={`opacity-50 cursor-not-allowed ${className}`}
      title={message}
      data-testid={testId}
    >
      {children}
    </Button>
  );
}
